import { HTTPError } from "ky";

let datadogLogs: typeof import("@datadog/browser-logs").datadogLogs | undefined;

async function getDatadogLogs() {
  if (!datadogLogs) {
    const mod = await import("@datadog/browser-logs");
    datadogLogs = mod.datadogLogs;
  }
  return datadogLogs;
}

interface LogOptions {
  action: string;
  error: unknown;
  context?: Record<string, unknown>;
}

function normalizeError(error: unknown): Error {
  if (error instanceof Error) return error;
  if (typeof error === "string") return new Error(error);
  return new Error(String(error));
}

function getUrl(): string {
  try {
    return window.location.href;
  } catch {
    return "";
  }
}

function extractHttpContext(error: unknown): Record<string, unknown> {
  if (!(error instanceof HTTPError)) return {};

  return {
    up_status: error.response.status,
    up_status_text: error.response.statusText,
    up_request_url: error.request.url,
  };
}

/**
 * Logs an error to Datadog with auto-enriched context.
 * Normalizes unknown errors, extracts HTTP metadata from ky {@link HTTPError},
 * and attaches the current page URL.
 */
function logError({ action, error, context }: LogOptions): void {
  const normalizedError = normalizeError(error);

  if (typeof window === "undefined") {
    console.error(`[${action}]`, normalizedError.message, context ?? "");
    return;
  }

  const enrichedContext: Record<string, unknown> = {
    up_url: getUrl(),
    ...extractHttpContext(error),
    ...context,
  };

  void getDatadogLogs()
    .then((dd) => dd.logger.error(action, enrichedContext, normalizedError))
    .catch(() =>
      console.error(`[${action}]`, normalizedError.message, enrichedContext),
    );
}

function logWarn({ action, error, context }: LogOptions): void {
  const normalizedError = normalizeError(error);

  if (typeof window === "undefined") {
    console.warn(`[${action}]`, normalizedError.message, context ?? "");
    return;
  }

  const enrichedContext: Record<string, unknown> = {
    up_url: getUrl(),
    ...extractHttpContext(error),
    ...context,
  };

  void getDatadogLogs()
    .then((dd) => dd.logger.warn(action, enrichedContext, normalizedError))
    .catch(() =>
      console.warn(`[${action}]`, normalizedError.message, enrichedContext),
    );
}

export { logError, logWarn };
export type { LogOptions };
